/**
 * activityDetailFormat — R5 / Stream FD (Feed-Detailtiefe)
 *
 * Reine Formatierungs-Helfer fuer die immer sichtbaren Detailzeilen des
 * Aktivitaets-Feeds. Bewusst ohne React/i18n-Abhaengigkeit, damit sie
 * unit-testbar bleiben; die Einheiten-Suffixe sind sprachneutral
 * (m/km/ft/mi/Mbit/s/ms/kB/MB).
 *
 * Entscheidung R5: Distanzen folgen der Nutzer-Einstellung Metric/Imperial
 * (account.use_metric), Geschwindigkeiten werden in Mbit/s und Latenzen in ms
 * angegeben (v1 zeigt Bytes/s ueber number_to_human_size — fuer Speedtests ist
 * Mbit/s die im Helium-Umfeld uebliche Einheit).
 */

const METERS_PER_FOOT = 0.3048
const FEET_PER_MILE = 5280

/** Zahl mit fester Nachkommastelle, ohne Locale-Rauschen in Tests. */
function round(value: number, decimals: number): string {
  return value.toFixed(decimals)
}

/**
 * Distanz in Metern → "12 m" / "1.2 km" bzw. "39 ft" / "0.8 mi".
 * null/undefined/NaN → null (Zeile wird dann nicht gerendert, v1-Verhalten).
 */
export function formatDistance(meters: number | null | undefined, useMetric: boolean): string | null {
  if (meters === null || meters === undefined || !Number.isFinite(meters)) return null

  if (useMetric) {
    if (Math.abs(meters) < 1000) return `${round(meters, 0)} m`
    return `${round(meters / 1000, 2)} km`
  }

  const feet = meters / METERS_PER_FOOT
  if (Math.abs(feet) < FEET_PER_MILE) return `${round(feet, 0)} ft`
  return `${round(feet / FEET_PER_MILE, 2)} mi`
}

/** Bits pro Sekunde → "48.00 Mbit/s". */
export function formatSpeedMbit(bps: number | null | undefined): string | null {
  if (bps === null || bps === undefined || !Number.isFinite(bps)) return null
  return `${round(bps / 1_000_000, 2)} Mbit/s`
}

/** Latenz → "31 ms". */
export function formatLatencyMs(ms: number | null | undefined): string | null {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return null
  return `${round(ms, 0)} ms`
}

/** Byte-Menge → "24.0 kB" (dezimal, wie number_to_human_size in v1). */
export function formatBytes(bytes: number | null | undefined): string | null {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return null
  const abs = Math.abs(bytes)
  if (abs < 1000) return `${round(bytes, 0)} B`
  if (abs < 1_000_000) return `${round(bytes / 1000, 1)} kB`
  if (abs < 1_000_000_000) return `${round(bytes / 1_000_000, 1)} MB`
  return `${round(bytes / 1_000_000_000, 2)} GB`
}

/** Lat/Lon-Paar → "52.51630, 13.37770"; null wenn eine Komponente fehlt. */
export function formatLatLon(
  lat: number | null | undefined,
  lon: number | null | undefined,
): string | null {
  if (lat === null || lat === undefined || !Number.isFinite(lat)) return null
  if (lon === null || lon === undefined || !Number.isFinite(lon)) return null
  return `${round(lat, 5)}, ${round(lon, 5)}`
}

/** Multiplikator → "1.0x"; 1 bleibt sichtbar (v1 zeigt ihn immer). */
export function formatMultiplier(value: number | null | undefined): string | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null
  return `${round(value, 2)}x`
}

/** listen_addrs kann String oder Array sein (Legacy-Rows). */
export function formatListenAddrs(value: string | string[] | null | undefined): string | null {
  if (value === null || value === undefined) return null
  if (Array.isArray(value)) {
    const joined = value.filter(Boolean).join(', ')
    return joined.length > 0 ? joined : null
  }
  const str = String(value).trim()
  return str.length > 0 ? str : null
}
